import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { updateWorkspaceMembers } from "src/ui/containers/clients/api";
import { emailRegex } from "src/ui/containers/unauthorized/components/LoginPage/LoginPage";
import { Modal } from "src/ui/shared/Modal/Modal";

import { PrimaryButton } from "@shared/CustomButton";
import { TextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const initialValues: FormValues = {
    email: "",
};

type FormValues = {
    email?: string;
};

interface Props {
    open: boolean;
    onClose: VoidFunction;
    workspaceId: number | null;
    clientId: number;
    onOwnerAdded?: () => void;
}

export const AddOwnerDialog = (props: Props) => {
    const { t } = useTranslation(["common", "modals"]);

    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && !form.getState().submitting) {
            await form.submit();
        }
    };

    const validate = (values: FormValues) => {
        const trimmedEmail = values.email?.trim();
        if (!trimmedEmail) {
            return { email: t("modals:addOwner.input.email.validation.empty") };
        }
        if (!emailRegex.test(trimmedEmail)) {
            return { email: t("modals:addOwner.input.email.validation.invalid") };
        }
    };

    const onSubmit = async (values: FormValues) => {
        const trimmedEmail = values.email?.trim();
        if (trimmedEmail && props.workspaceId !== null) {
            try {
                await updateWorkspaceMembers({
                    workspaceId: props.workspaceId,
                    clientId: props.clientId,
                    addedMembers: { [trimmedEmail]: "Owner" },
                });
                showSuccessToast(t("modals:addOwner.toast.ownerAddedSuccess"));
                props.onOwnerAdded?.();
                props.onClose();
            } catch (error) {
                const err = error as Error;
                showErrorToast(err.message || t("modals:addOwner.toast.ownerAddFailed"));
            }
        }
    };

    return (
        <Form<FormValues>
            {...{ onSubmit, validate, initialValues }}
            render={({ handleSubmit, submitting, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        title={t("modals:addOwner.title")}
                        open={props.open}
                        onClose={props.onClose}
                        onKeyDown={(e) => onKeyDown(e, form)}
                        endButton={
                            <PrimaryButton onClick={form.submit} disabled={submitting} loading={submitting}>
                                {t("common:button.add")}
                            </PrimaryButton>
                        }
                    >
                        <TextField name="email" label={t("modals:addOwner.input.email.caption")} required />
                    </Modal>
                </form>
            )}
        />
    );
};
